import { EditorSelection, Prec, type ChangeSpec } from '@codemirror/state';
import { keymap, type Command, type KeyBinding } from '@codemirror/view';

/**
 * Markdown-aware editing commands for the CodeMirror plaintext view, exposed as
 * a reusable `keymap` extension. Commands operate on every selection range so
 * multi-cursor edits work, and toggle (wrap if unwrapped, unwrap if already
 * wrapped) for inline marks. Heading commands rewrite the current line's
 * leading `#`s. All bindings use `mac`/`win`/`linux` specs so CodeMirror's own
 * `keymap` picks the right modifier per platform — no manual mod detection
 * needed here.
 */

/** Toggle an inline wrapper (e.g. `**…**`) around each selection range. */
function wrapInline(open: string, close: string): Command {
  return (view) => {
    const { state } = view;
    const changes: ChangeSpec[] = [];
    const ranges = state.selection.ranges.map((range) => {
      const selected = state.sliceDoc(range.from, range.to);
      // Already-wrapped INSIDE the selection? -> unwrap by trimming the markers.
      if (
        selected.length >= open.length + close.length &&
        selected.startsWith(open) &&
        selected.endsWith(close)
      ) {
        const inner = selected.slice(open.length, selected.length - close.length);
        changes.push({ from: range.from, to: range.to, insert: inner });
        return EditorSelection.range(range.from, range.from + inner.length);
      }
      // Markers immediately OUTSIDE the selection? -> unwrap them.
      const before = state.sliceDoc(Math.max(0, range.from - open.length), range.from);
      const after = state.sliceDoc(range.to, Math.min(state.doc.length, range.to + close.length));
      if (before === open && after === close) {
        changes.push({ from: range.from - open.length, to: range.from, insert: '' });
        changes.push({ from: range.to, to: range.to + close.length, insert: '' });
        return EditorSelection.range(range.from - open.length, range.to - open.length);
      }
      // Otherwise wrap. Empty selection -> place the cursor between the markers.
      changes.push({ from: range.from, insert: open });
      changes.push({ from: range.to, insert: close });
      if (range.empty) {
        const pos = range.from + open.length;
        return EditorSelection.cursor(pos);
      }
      return EditorSelection.range(range.from + open.length, range.to + open.length);
    });
    view.dispatch(
      state.update({
        changes,
        selection: EditorSelection.create(ranges, state.selection.mainIndex),
        scrollIntoView: true,
        userEvent: 'input.format',
      }),
    );
    view.focus();
    return true;
  };
}

/** Turn each selection into a markdown link `[sel](url)`, selecting `url`. */
const insertLink: Command = (view) => {
  const { state } = view;
  const changes: ChangeSpec[] = [];
  const placeholder = 'url';
  const ranges = state.selection.ranges.map((range) => {
    const text = state.sliceDoc(range.from, range.to) || 'text';
    const insert = `[${text}](${placeholder})`;
    changes.push({ from: range.from, to: range.to, insert });
    // Select the placeholder url so the user can type the target immediately.
    const urlStart = range.from + 1 + text.length + 2; // `[` + text + `](`
    return EditorSelection.range(urlStart, urlStart + placeholder.length);
  });
  view.dispatch(
    state.update({
      changes,
      selection: EditorSelection.create(ranges, state.selection.mainIndex),
      scrollIntoView: true,
      userEvent: 'input.format',
    }),
  );
  view.focus();
  return true;
};

/** Set (or clear, when `level` is 0) the heading level of each touched line. */
function setHeading(level: number): Command {
  return (view) => {
    const { state } = view;
    const changes: ChangeSpec[] = [];
    const seen = new Set<number>();
    for (const range of state.selection.ranges) {
      const startLine = state.doc.lineAt(range.from).number;
      const endLine = state.doc.lineAt(range.to).number;
      for (let n = startLine; n <= endLine; n++) {
        if (seen.has(n)) continue;
        seen.add(n);
        const line = state.doc.line(n);
        // Match existing leading `#`s plus a single trailing space.
        const existing = /^(#{1,6})(\s+)?/.exec(line.text);
        const existingLen = existing ? existing[0].length : 0;
        const prefix = level > 0 ? `${'#'.repeat(level)} ` : '';
        changes.push({ from: line.from, to: line.from + existingLen, insert: prefix });
      }
    }
    view.dispatch(
      state.update({ changes, scrollIntoView: true, userEvent: 'input.format' }),
    );
    view.focus();
    return true;
  };
}

const FORMAT_BINDINGS: KeyBinding[] = [
  { key: 'Mod-b', run: wrapInline('**', '**'), preventDefault: true },
  { key: 'Mod-i', run: wrapInline('*', '*'), preventDefault: true },
  { key: 'Mod-u', run: wrapInline('<u>', '</u>'), preventDefault: true },
  { key: 'Mod-e', run: wrapInline('`', '`'), preventDefault: true },
  { key: 'Mod-Shift-x', run: wrapInline('~~', '~~'), preventDefault: true },
  { key: 'Mod-k', run: insertLink, preventDefault: true },
  { key: 'Mod-1', run: setHeading(1), preventDefault: true },
  { key: 'Mod-2', run: setHeading(2), preventDefault: true },
  { key: 'Mod-3', run: setHeading(3), preventDefault: true },
  { key: 'Mod-4', run: setHeading(4), preventDefault: true },
  { key: 'Mod-5', run: setHeading(5), preventDefault: true },
  { key: 'Mod-6', run: setHeading(6), preventDefault: true },
  { key: 'Mod-0', run: setHeading(0), preventDefault: true },
];

/**
 * CodeMirror extension adding markdown formatting + heading shortcuts. `Mod-`
 * resolves to ⌘ on macOS and Ctrl elsewhere, matching the rest of the app.
 * `Prec.high` ensures our bindings win over any default keymap that might claim
 * the same chord. (Undo/redo come from `basicSetup`'s history keymap — `Mod-z`,
 * `Mod-Shift-z`, `Ctrl-y` — and are left untouched.)
 */
export const markdownShortcuts = Prec.high(keymap.of(FORMAT_BINDINGS));
